import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/planner/lib/apiClient";
import { productLabel } from "@/planner/lib/format";
import { useAuth } from "@/planner/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlotTile } from "@/planner/components/SlotTile";
import CoolerView from "@/planner/components/CoolerView";
import PhotoPicker from "@/planner/components/PhotoPicker";
import MachineRowsDialog from "@/planner/components/MachineRowsDialog";
import SandstarPanel from "@/planner/components/SandstarPanel";
import SandstarLibraryAdd from "@/planner/components/SandstarLibraryAdd";
import { fileUrl } from "@/planner/lib/apiClient";
import { priceForMargin, marginOnCost, unitCost, shelfPrice, money, markupFor, markupLabel, ruleFromSettings, DEFAULT_RULE } from "@/planner/lib/pricing";
import { catColor } from "@/planner/lib/vendy";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, Info, GripVertical, Printer, CheckCheck, Search, ArrowUpToLine, Loader2, Check, Wand2, Bookmark, Trash2, Refrigerator, LayoutGrid, FolderOpen, Save, Tag, ListChecks, X, ImageIcon, Package, Star, SlidersHorizontal } from "lucide-react";
import { DndContext, DragOverlay, useDraggable, useDroppable, MouseSensor, TouchSensor, useSensor, useSensors, pointerWithin, rectIntersection } from "@dnd-kit/core";

// Drop on the spot under the pointer/finger; shelf spots are small, so the dragged chip's
// rectangle alone often overlaps the wrong neighbor.
const dropUnderPointer = (args) => {
  const hits = pointerWithin(args);
  return hits.length ? hits : rectIntersection(args);
};

// A planogram slot that is both a drop target and (when filled) a drag source.
// Used for palette->slot assignment and slot->slot moves/swaps.
const PlanogramSlot = ({ s, product, dndEnabled, addMode, selected, showCounts, canRemove, onClick, onMinus, onPlus, onRemove }) => {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `slot:${s.slot_id}`, disabled: !dndEnabled });
  const { setNodeRef: setDragRef, listeners, attributes, isDragging } = useDraggable({ id: `slotsrc:${s.slot_id}`, disabled: !dndEnabled || !product });
  return (
    <div ref={setDropRef} className="flex flex-col gap-1" style={{ opacity: isDragging ? 0.4 : undefined }}>
      <SlotTile
        slot={s}
        product={product}
        selected={selected}
        dragOver={isOver}
        addMode={addMode}
        onClick={onClick}
        dragProps={{ ref: setDragRef, ...listeners, ...attributes }}
      />
      {showCounts && (
        <div className="flex items-center justify-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" data-testid={`slot-minus-${s.slot_id}`} onClick={onMinus}><Minus className="h-3 w-3" /></Button>
          <span className="font-mono text-xs w-6 text-center">{s.current}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" data-testid={`slot-plus-${s.slot_id}`} onClick={onPlus}><Plus className="h-3 w-3" /></Button>
          {canRemove && product && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10" title="Remove item from slot" data-testid={`slot-remove-${s.slot_id}`} onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>
          )}
        </div>
      )}
    </div>
  );
};

// A draggable product chip in the sectioned palette. Under a product heading (child) it shows just
// the flavor; on its own it shows the name with the flavor underneath. Size and price always show.
const PaletteItem = ({ p, picked, onPick, onFavorite, compact, child, price }) => {
  const c = catColor(p.category);
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: `prod:${p.id}` });
  const flavor = (p.variant || "").trim();
  const title = child ? (flavor || "Original") : p.name;
  const sub = child ? "" : flavor;
  const img = p.image_path ? fileUrl(p.image_path) : null;
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onPick && onPick(p.id)}
      title={[productLabel(p), p.size].filter(Boolean).join(" · ")}
      data-testid={`palette-product-${p.id}`}
      className={`flex cursor-grab items-center gap-2 rounded-md border px-2 py-1.5 text-xs active:cursor-grabbing hover:bg-muted ${compact ? "w-48 shrink-0 touch-manipulation" : "touch-none"} ${picked ? "ring-2 ring-sky-500 bg-sky-50" : ""}`}
      style={{ borderLeft: `4px solid ${c.stripe}`, opacity: isDragging ? 0.4 : undefined }}
    >
      {img && <img src={img} alt="" className="h-8 w-8 shrink-0 rounded object-contain bg-white" />}
      <div className="min-w-0 flex-1">
        {!child && <div className={`leading-tight font-semibold ${compact ? "truncate" : "break-words"}`}>{title}</div>}
        {sub && <div className={`leading-tight text-muted-foreground ${compact ? "truncate" : "break-words"}`}>{sub}</div>}
        <div className={`flex flex-wrap items-center gap-1 ${child ? "" : "mt-0.5"}`}>
          {child && <span className="mr-0.5 break-words leading-tight">{title}</span>}
          {p.size
            ? <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`palette-size-${p.id}`}>{p.size}</span>
            : <span className="text-[10px] italic text-muted-foreground">no size</span>}
          {price != null && <span className="text-[10px] text-muted-foreground">{money(price)}</span>}
          {p.on_hand > 0 && <span className="text-[10px] font-semibold text-green-700" data-testid={`palette-onhand-${p.id}`}>{p.on_hand} on hand</span>}
        </div>
      </div>
      {onFavorite && (
        <button
          type="button"
          aria-label={p.favorite ? `Unfavorite ${productLabel(p)}` : `Favorite ${productLabel(p)}`}
          title={p.favorite ? "Remove from favorites" : "Add to favorites"}
          data-testid={`palette-fav-${p.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onFavorite(p); }}
          className="shrink-0 self-start rounded p-0.5 hover:bg-amber-100"
        >
          <Star className={`h-3.5 w-3.5 ${p.favorite ? "fill-amber-400 text-amber-500" : "text-muted-foreground/50"}`} />
        </button>
      )}
    </div>
  );
};

// Runs of products sharing a name, in list order: [{ name, items }].
const byProductName = (items) => {
  const out = [];
  const at = new Map();
  items.forEach((p) => {
    const k = (p.name || "").trim().toLowerCase();
    if (!at.has(k)) { at.set(k, out.length); out.push({ name: p.name, items: [] }); }
    out[at.get(k)].items.push(p);
  });
  return out;
};

// What to bring and load at the location, shelf by shelf, plus what a full cooler earns.
const LoadSheet = ({ rowGroups, pmap, globalMargin }) => {
  let units = 0; let loadCost = 0; let fullCost = 0; let fullSales = 0;
  const rows = rowGroups.map(([letter, slots]) => [letter, slots.filter((s) => s.product_id && pmap[s.product_id]).map((s) => {
    const p = pmap[s.product_id];
    const par = s.par || 0; const cur = s.current || 0; const load = Math.max(0, par - cur);
    const cost = unitCost(p); const sell = shelfPrice(p, globalMargin);
    units += load;
    if (cost != null) { loadCost += cost * load; fullCost += cost * par; }
    if (sell != null) fullSales += sell * par;
    return { s, p, par, cur, load, sell };
  })]).filter(([, items]) => items.length);
  if (!rows.length) return null;
  return (
    <Card className="mt-4 p-4" data-testid="load-sheet">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-heading font-semibold"><ListChecks className="h-4 w-4 text-primary" />Load sheet</div>
        <div className="text-xs text-muted-foreground" data-testid="load-sheet-totals">
          Bring <b className="text-foreground">{units}</b> units ({money(loadCost)}) · full cooler sells for <b className="text-foreground">{money(fullSales)}</b>, profit <b className="text-green-700">{money(fullSales - fullCost)}</b>
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {rows.map(([letter, items]) => (
          <div key={letter} className="rounded-md border">
            <div className="border-b bg-muted/50 px-2.5 py-1 text-xs font-semibold">Shelf {letter}</div>
            {items.map(({ s, p, par, cur, load, sell }) => (
              <div key={s.slot_id} className="flex items-center gap-2 border-b px-2.5 py-1.5 text-xs last:border-b-0" data-testid={`load-row-${s.slot_id}`}>
                <span className="w-7 shrink-0 font-mono text-muted-foreground">{s.slot_id}</span>
                <span className="min-w-0 flex-1 truncate font-medium">{productLabel(p)}{p.size ? <span className="text-muted-foreground"> · {p.size}</span> : null}</span>
                <span className="shrink-0 font-mono">{sell != null ? money(sell) : "$?"}</span>
                <span className="w-10 shrink-0 text-right font-mono text-muted-foreground">{cur}/{par}</span>
                <span className={`w-14 shrink-0 rounded px-1 text-center font-semibold ${load ? "bg-amber-100 text-amber-900" : "text-green-700"}`}>{load ? `load ${load}` : "full"}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default function MachineDetail() {
  const { id } = useParams();
  const { canEdit, user } = useAuth();
  const [machine, setMachine] = useState(null);
  const [products, setProducts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [sel, setSel] = useState(null); // selected slot for assignment
  const [assign, setAssign] = useState({ product_id: "none", par: 10, current: 0 });
  const [activeDrag, setActiveDrag] = useState(null); // { type: 'prod'|'slot', product, slotId }
  const [paletteQuery, setPaletteQuery] = useState("");
  const [paletteSort, setPaletteSort] = useState(() => { try { return localStorage.getItem("planner_palette_sort") || "type"; } catch (_) { return "type"; } });
  const changeSort = (v) => { setPaletteSort(v); try { localStorage.setItem("planner_palette_sort", v); } catch (_) { /* ignore */ } };
  const [paletteCat, setPaletteCat] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [rowsOpen, setRowsOpen] = useState(false);
  const [view, setView] = useState(() => { try { return localStorage.getItem("planner_machine_view") || "cooler"; } catch (_) { return "cooler"; } });
  const changeView = (v) => { setView(v); try { localStorage.setItem("planner_machine_view", v); } catch (_) { /* ignore */ } };
  const [picked, setPicked] = useState(null); // product chosen in the palette for tap-to-place
  const togglePick = (pid) => setPicked((cur) => (cur === pid ? null : pid));
  const [globalMargin, setGlobalMargin] = useState(DEFAULT_RULE);
  const [price, setPrice] = useState({ cost: "", margin: "", vend: "" });
  const [savingPrice, setSavingPrice] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [photoFor, setPhotoFor] = useState(null); // product, or { queue } for the walk-through
  const onPhotoSaved = (p) => setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, ...p } : x)));

  // Unified pointer + touch dragging. MouseSensor handles desktop clicks vs drags
  // (5px threshold keeps taps working); TouchSensor uses a short press-delay so a
  // normal finger swipe still scrolls the planogram and only a deliberate
  // press-and-hold starts a slot drag on mobile.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );

  const load = async () => {
    const [m, p] = await Promise.all([api.get(`/machines/${id}`), api.get("/products")]);
    setMachine(m.data); setProducts(p.data);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/settings").then((r) => setGlobalMargin(ruleFromSettings(r.data))).catch(() => {}); }, []);

  const pmap = Object.fromEntries(products.map((p) => [p.id, p]));

  // Picking a different product in the slot dialog shows that product's pricing.
  useEffect(() => {
    if (!sel) return;
    const p = products.find((x) => x.id === assign.product_id);
    setPrice({
      cost: unitCost(p) != null ? String(Math.round(unitCost(p) * 1000) / 1000) : "",
      margin: p?.target_margin_pct != null ? String(p.target_margin_pct) : "",
      vend: p?.vend_price != null ? String(p.vend_price) : "",
    });
  }, [sel, assign.product_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascading product picker for slot assignment: name -> flavor -> size.
  const flVal = (v) => (v === "" ? "__plain__" : v);
  const flRaw = (v) => (v === "__plain__" ? "" : v);
  const szVal = (v) => (v === "" ? "__nosize__" : v);
  const szRaw = (v) => (v === "__nosize__" ? "" : v);
  const selProd = products.find((p) => p.id === assign.product_id) || null;
  const favNames = new Set(products.filter((p) => p.favorite).map((p) => p.name));
  const productNames = [...new Set(products.map((p) => p.name))]
    .sort((a, b) => (favNames.has(b) - favNames.has(a)) || a.localeCompare(b));
  const curName = selProd?.name || "";
  const curFlavor = selProd?.variant || "";
  const curSize = selProd?.size || "";
  const flavorOpts = [...new Set(products.filter((p) => p.name === curName).map((p) => p.variant || ""))];
  const sizeOpts = [...new Set(products.filter((p) => p.name === curName && (p.variant || "") === curFlavor).map((p) => p.size || ""))];
  const showFlavor = !!selProd && (flavorOpts.length > 1 || flavorOpts.some((f) => f !== ""));
  const pickName = (name) => {
    if (name === "none") return setAssign((a) => ({ ...a, product_id: "none" }));
    const f = products.find((p) => p.name === name);
    if (f) setAssign((a) => ({ ...a, product_id: f.id }));
  };
  const pickFlavor = (fl) => {
    const m = products.find((p) => p.name === curName && (p.variant || "") === fl && (p.size || "") === curSize)
      || products.find((p) => p.name === curName && (p.variant || "") === fl);
    if (m) setAssign((a) => ({ ...a, product_id: m.id }));
  };
  const pickSize = (sz) => {
    const m = products.find((p) => p.name === curName && (p.variant || "") === curFlavor && (p.size || "") === sz);
    if (m) setAssign((a) => ({ ...a, product_id: m.id }));
  };


  const dropProduct = (slotId, productId) => {
    const prod = pmap[productId];
    // Par is machine/slot specific — keep the slot's template-derived capacity.
    const slots = machine.slots.map((s) => s.slot_id === slotId
      ? { ...s, product_id: productId, par: s.par || 10 }
      : s);
    setMachine({ ...machine, slots }); setDirty(true);
    toast.success(`${prod?.name || "Product"} → ${slotId}`);
  };

  // Move/swap between two slots. Product_id AND current move together; par stays
  // with each slot. Dropping onto an occupied slot swaps the two; dropping onto
  // an empty slot moves the product and clears the source.
  const swapSlots = (fromId, toId) => {
    const from = machine.slots.find((s) => s.slot_id === fromId);
    const to = machine.slots.find((s) => s.slot_id === toId);
    if (!from) return;
    const slots = machine.slots.map((s) => {
      if (s.slot_id === fromId) return { ...s, product_id: to?.product_id ?? null, current: to?.current ?? 0 };
      if (s.slot_id === toId) return { ...s, product_id: from.product_id ?? null, current: from.current ?? 0 };
      return s;
    });
    setMachine({ ...machine, slots }); setDirty(true);
    const fromName = from.product_id ? (pmap[from.product_id]?.name || "Product") : "Empty";
    toast.success(to?.product_id ? `Swapped ${fromId} \u2194 ${toId}` : `${fromName} \u2192 ${toId}`);
  };

  const dndEnabled = canEdit && !addMode;

  const handleDragStart = (e) => {
    const idStr = String(e.active.id);
    if (idStr.startsWith("prod:")) {
      setActiveDrag({ type: "prod", product: pmap[idStr.slice(5)] || null });
    } else if (idStr.startsWith("slotsrc:")) {
      const sid = idStr.slice(8);
      const slot = machine.slots.find((s) => s.slot_id === sid);
      setActiveDrag({ type: "slot", slotId: sid, product: slot?.product_id ? pmap[slot.product_id] : null });
    }
  };

  const handleDragEnd = (e) => {
    const { active, over } = e;
    setActiveDrag(null);
    if (!over) return;
    const overId = String(over.id);
    if (!overId.startsWith("slot:")) return;
    const toId = overId.slice(5);
    const actId = String(active.id);
    if (actId.startsWith("prod:")) {
      dropProduct(toId, actId.slice(5));
    } else if (actId.startsWith("slotsrc:")) {
      const fromId = actId.slice(8);
      if (fromId !== toId) swapSlots(fromId, toId);
    }
  };

  const priceFormFor = (p) => ({
    cost: unitCost(p) != null ? String(Math.round(unitCost(p) * 1000) / 1000) : "",
    margin: p?.target_margin_pct != null ? String(p.target_margin_pct) : "",
    vend: p?.vend_price != null ? String(p.vend_price) : "",
  });

  const openSlot = (slot) => {
    if (!canEdit) return;
    setSel(slot);
    setAssign({ product_id: slot.product_id || "none", par: slot.par ?? 10, current: slot.current ?? 0 });
    setPrice(priceFormFor(slot.product_id ? pmap[slot.product_id] : null));
  };

  // With a product picked in the palette, tapping a shelf spot places it there (phone-friendly).
  const clickSlot = (slot) => {
    if (addMode && slot.product_id) return quickCount(slot, 1);
    if (picked && canEdit && !addMode) return dropProduct(slot.slot_id, picked);
    return openSlot(slot);
  };

  // Save cost / margin / price for the item in the open slot. A blank price means "use the rule".
  const savePricing = async () => {
    const pid = assign.product_id;
    if (!pid || pid === "none") return;
    const body = {};
    if (price.cost !== "") body.cost = Number(price.cost);
    body.target_margin_pct = price.margin === "" ? null : Number(price.margin);
    if (price.vend === "") body.use_rule = true; else body.vend_price = Number(price.vend);
    setSavingPrice(true);
    try {
      const { data } = await api.patch(`/products/${pid}/pricing`, body);
      setProducts((list) => list.map((p) => (p.id === pid ? { ...p, ...data } : p)));
      setPrice(priceFormFor(data));
      toast.success(`${data.name} priced at ${money(data.vend_price)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the price"); }
    finally { setSavingPrice(false); }
  };

  const loadTemplates = async () => {
    try { const { data } = await api.get("/loadouts"); setTemplates(data); } catch (_) { toast.error("Could not load templates"); }
  };
  const openTemplates = () => { setTplOpen(true); setTplName(""); loadTemplates(); };
  const saveTemplate = async () => {
    if (!tplName.trim()) return toast.error("Name the template first");
    if (dirty) await persistPlanogram(false);
    try {
      const { data } = await api.post("/loadouts", { name: tplName.trim(), machine_id: id });
      toast.success(`Saved "${data.name}" (${data.item_count} spots)`);
      setTplName(""); loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const applyTemplate = async (t) => {
    if (!window.confirm(`Load "${t.name}" into ${machine.name}? This replaces what's on the shelves now.`)) return;
    if (dirty) await persistPlanogram(false);
    try {
      const { data } = await api.post(`/loadouts/${t.id}/apply`, { machine_id: id });
      setMachine(data.machine); setDirty(false); setTplOpen(false);
      toast.success(`Loaded "${t.name}" (${data.placed} spots${data.skipped ? `, ${data.skipped} didn't fit this machine` : ""})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the template"); }
  };
  const addSamples = async () => {
    try {
      const { data } = await api.post("/loadouts/samples");
      if (data.products_added) {
        const { data: prods } = await api.get("/products");
        setProducts(prods);
      }
      toast.success(`Added ${data.templates.length} sample templates${data.products_added ? ` and ${data.products_added} new products` : ""}`);
      loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the samples"); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the template "${t.name}"?`)) return;
    try { await api.delete(`/loadouts/${t.id}`); loadTemplates(); } catch (_) { toast.error("Delete failed"); }
  };

  const applyAssign = () => {
    const slots = machine.slots.map((s) => s.slot_id === sel.slot_id
      ? { ...s, product_id: assign.product_id === "none" ? null : assign.product_id, par: Number(assign.par), current: Number(assign.current) }
      : s);
    setMachine({ ...machine, slots }); setDirty(true); setSel(null);
  };

  // Clear a slot: drop its product and reset its count to 0 (par is kept so the
  // slot's capacity stays). Autosaves like any other edit.
  const removeSlot = (slotId) => {
    const slots = machine.slots.map((s) => s.slot_id === slotId
      ? { ...s, product_id: null, current: 0 }
      : s);
    setMachine({ ...machine, slots }); setDirty(true); setSel(null);
    toast.success(`Removed item from ${slotId}`);
  };

  // --- Autosave -----------------------------------------------------------
  // Any planogram edit (assign, move/swap, remove, par change, quick count)
  // flips `dirty`; a debounced effect persists it automatically so operators
  // never lose changes or have to remember to hit Save.
  const machineRef = useRef(machine);
  machineRef.current = machine;
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const persistPlanogram = useCallback(async (announce = false) => {
    const snap = machineRef.current;
    if (!snap) return;
    savingRef.current = true; setSaving(true);
    try {
      const { data } = await api.put(`/machines/${id}/planogram`, {
        slots: snap.slots.map((s) => ({ slot_id: s.slot_id, product_id: s.product_id, par: s.par, current: s.current })),
        updated_at: snap.updated_at,
      });
      if (machineRef.current === snap) {
        // No newer edits landed while we were saving — adopt server truth.
        setMachine(data); setDirty(false);
      } else {
        // Fresh edits arrived mid-save: keep them, just refresh the version
        // token so the next autosave passes the concurrency check.
        setMachine((m) => ({ ...m, updated_at: data.updated_at }));
      }
      if (announce) toast.success("Planogram saved");
    } catch (e) {
      if (e?.response?.status === 409) { toast.error("Changed by someone else — reloading"); load(); }
      else if (announce) toast.error(e?.response?.data?.detail || "Save failed");
      // Silent autosave failures stay dirty and retry on the next edit.
    } finally {
      savingRef.current = false; setSaving(false);
    }
  }, [id]);

  useEffect(() => {
    if (!dirty || !canEdit || savingRef.current) return;
    const t = setTimeout(() => persistPlanogram(false), 900);
    return () => clearTimeout(t);
  }, [dirty, machine, canEdit, persistPlanogram]);

  const quickCount = async (slot, delta) => {
    const next = Math.max(0, (slot.current ?? 0) + delta);
    const slots = machine.slots.map((s) => (s.slot_id === slot.slot_id ? { ...s, current: next } : s));
    // If the planogram has unsaved edits (e.g. a product just dragged in but
    // not saved), only update locally so the assignment isn't wiped by the
    // server response. Otherwise persist the count immediately.
    if (dirty) {
      setMachine({ ...machine, slots });
      return;
    }
    try {
      // Use the field-stocking endpoint so stockers can adjust counts too
      // (it also records an audit entry). Par is never touched here.
      const { data } = await api.put(`/machines/${id}/stock`, { slot_id: slot.slot_id, current: next });
      setMachine(data);
    } catch (e) {
      setMachine({ ...machine, slots }); setDirty(true);
      toast.error("Couldn't save the count — retrying automatically");
    }
  };

  const undoFill = async () => {
    try {
      const { data } = await api.post(`/machines/${id}/undo-fill`);
      setMachine(data); setDirty(false);
      toast.success("Fill undone: counts and backstock are back");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't undo the fill"); }
  };

  const fillToPar = async () => {
    const units = (machine.slots || []).reduce((n, s) => n + (s.product_id ? Math.max(0, (s.par || 0) - (s.current || 0)) : 0), 0);
    if (!window.confirm(`Mark every slot as filled to par? ${units} unit${units === 1 ? "" : "s"} come out of backstock.`)) return;
    try {
      if (dirty) await persistPlanogram(false);
      const { data } = await api.post(`/machines/${id}/fill-to-par`);
      setMachine(data); setDirty(false);
      const n = (data.slots || []).filter((s) => s.product_id).length;
      toast.success(`Filled ${n} slot${n !== 1 ? "s" : ""} to par`, { duration: 20000, action: { label: "Undo", onClick: undoFill } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Fill failed"); }
  };

  const [autoFilling, setAutoFilling] = useState(false);
  const autoFill = async () => {
    setAutoFilling(true);
    try {
      const { data } = await api.post(`/machines/${id}/auto-fill`);
      setMachine(data.machine); setDirty(false);
      toast.success(data.filled > 0 ? `Auto-filled ${data.filled} empty slot${data.filled !== 1 ? "s" : ""}` : "No empty slots to fill");
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-fill failed"); }
    finally { setAutoFilling(false); }
  };

  const saveAsStarter = async () => {
    const assigned = (machine.slots || []).filter((s) => s.product_id).length;
    if (assigned === 0) return toast.error("Assign some products first, then save as starter");
    if (!window.confirm(`Save this layout (${assigned} products) as the starter for NEW machines? It replaces any previous starter.`)) return;
    try {
      const { data } = await api.post(`/machines/${id}/save-as-starter`);
      toast.success(`Saved starter — new machines will start with ${data.saved} products`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  if (!machine) return <div className="text-muted-foreground">Loading…</div>;

  const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const rowLabel = (letter) => (machine.layout || [])[LETTERS.indexOf(letter)]?.label || "";
  const rowColor = (letter) => (machine.layout || [])[LETTERS.indexOf(letter)]?.color || "";
  const rowDepth = (letter) => (machine.layout || [])[LETTERS.indexOf(letter)]?.depth || 0;
  // Set every slot's par in a row to the row's physical capacity (template
  // depth). Falls back to the largest par already in the row when a machine
  // has no layout metadata.
  const setRowToCapacity = (letter, rowSlots) => {
    const cap = rowDepth(letter) || Math.max(1, ...rowSlots.map((s) => s.par || 0));
    const ids = new Set(rowSlots.map((s) => s.slot_id));
    const slots = machine.slots.map((s) => (ids.has(s.slot_id) ? { ...s, par: cap } : s));
    setMachine({ ...machine, slots }); setDirty(true);
    toast.success(`Row ${letter} pars set to ${cap}`);
  };
  const hasProducts = (machine.slots || []).some((s) => s.product_id);

  // Palette: what's in backstock first (ready to load), then favorites, then grouped by type (in legend order) or one list in the chosen order.
  const CATEGORY_ORDER = ["Soda", "Energy", "Sports", "Water", "Juice", "Tea", "Chips", "Candy", "Protein", "Other"];
  const catRank = (c) => { const i = CATEGORY_ORDER.indexOf(c || "Other"); return i === -1 ? CATEGORY_ORDER.length : i; };
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "") || (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9)
    || (a.variant || "").localeCompare(b.variant || "")
    || (sizeOz(a) ?? 1e9) - (sizeOz(b) ?? 1e9) || (a.size || "").localeCompare(b.size || "");
  const num = (v) => (v == null || Number.isNaN(v) ? null : v);
  const lastIfNull = (a, b, dir) => (a == null && b == null ? 0 : a == null ? 1 : b == null ? -1 : dir * (a - b));
  const sizeOz = (p) => { const m = String(p.size || "").match(/([\d.]+)\s*(oz|ml|l)\b/i); if (!m) return null; const n = Number(m[1]); return m[2].toLowerCase() === "oz" ? n : m[2].toLowerCase() === "ml" ? n / 29.57 : n * 33.81; };
  const SORTS = {
    type: { label: "Type", cmp: (a, b) => catRank(a.category) - catRank(b.category) || byName(a, b) },
    name: { label: "Name A–Z", cmp: byName },
    price_desc: { label: "Price: high to low", cmp: (a, b) => lastIfNull(num(shelfPrice(a, globalMargin)), num(shelfPrice(b, globalMargin)), -1) || byName(a, b) },
    price_asc: { label: "Price: low to high", cmp: (a, b) => lastIfNull(num(shelfPrice(a, globalMargin)), num(shelfPrice(b, globalMargin)), 1) || byName(a, b) },
    margin: { label: "Best margin", cmp: (a, b) => lastIfNull(marginOnCost(unitCost(a), shelfPrice(a, globalMargin)), marginOnCost(unitCost(b), shelfPrice(b, globalMargin)), -1) || byName(a, b) },
    size: { label: "Size: largest first", cmp: (a, b) => lastIfNull(sizeOz(a), sizeOz(b), -1) || byName(a, b) },
    recent: { label: "Newest added", cmp: (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")) },
  };
  const paletteCats = CATEGORY_ORDER.filter((c) => products.some((p) => (p.category || "Other") === c))
    .concat([...new Set(products.map((p) => p.category || "Other"))].filter((c) => !CATEGORY_ORDER.includes(c)).sort());
  const favCount = products.filter((p) => p.favorite).length;
  const paletteGroups = (() => {
    const q = paletteQuery.trim().toLowerCase();
    const cmp = (SORTS[paletteSort] || SORTS.type).cmp;
    const filtered = products.filter((p) => (!q || `${p.name || ""} ${p.variant || ""} ${p.size || ""}`.toLowerCase().includes(q))
      && (paletteCat === "all" || (p.category || "Other") === paletteCat)
      && (!favOnly || p.favorite)).sort(cmp);
    const stocked = filtered.filter((p) => p.on_hand > 0);
    const favs = filtered.filter((p) => p.favorite && !(p.on_hand > 0));
    const rest = filtered.filter((p) => !p.favorite && !(p.on_hand > 0));
    const groups = [...(stocked.length ? [["In backstock", stocked]] : []), ...(favs.length ? [["Favorites", favs]] : [])];
    if (paletteSort !== "type") return rest.length ? [...groups, [paletteCat === "all" ? "All products" : paletteCat, rest]] : groups;
    const map = {};
    rest.forEach((p) => { const c = p.category || "Other"; (map[c] = map[c] || []).push(p); });
    return [...groups, ...Object.keys(map).sort((a, b) => catRank(a) - catRank(b) || a.localeCompare(b)).map((k) => [k, map[k]])];
  })();
  const toggleFavorite = async (p) => {
    const favorite = !p.favorite;
    setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, favorite } : x)));
    try { await api.put(`/products/${p.id}/favorite`, { favorite }); }
    catch (e) {
      setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, favorite: !favorite } : x)));
      toast.error("Could not update favorite");
    }
  };
  const paletteControls = (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="palette-controls">
      <Select value={paletteSort} onValueChange={changeSort}>
        <SelectTrigger className="h-8 w-full text-xs" data-testid="palette-sort-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>Sort: {v.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={paletteCat} onValueChange={setPaletteCat}>
        <SelectTrigger className="h-8 min-w-0 flex-1 text-xs" data-testid="palette-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All types</SelectItem>
          {paletteCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button type="button" size="sm" variant={favOnly ? "default" : "outline"} className="h-8 px-2 text-xs" data-testid="palette-fav-only"
        title="Show only favorites" onClick={() => setFavOnly((v) => !v)}>
        <Star className={`mr-1 h-3.5 w-3.5 ${favOnly ? "fill-current" : ""}`} />{favCount}
      </Button>
    </div>
  );

  // Group slots by their row letter (A1..A5, B1..B8, ...) so rows can each have
  // a different number of slots (variable-row templates like the Sandstar Ark).
  const rowGroups = (() => {
    const map = {};
    (machine.slots || []).forEach((s) => {
      const letter = (String(s.slot_id).match(/^[A-Za-z]+/) || ["?"])[0];
      (map[letter] = map[letter] || []).push(s);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (parseInt(String(a.slot_id).replace(/^[A-Za-z]+/, ""), 10) || 0) - (parseInt(String(b.slot_id).replace(/^[A-Za-z]+/, ""), 10) || 0))
    );
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  })();

  const slotCell = (s) => (
    <PlanogramSlot
      key={s.slot_id}
      s={s}
      product={s.product_id ? pmap[s.product_id] : null}
      dndEnabled={dndEnabled}
      addMode={addMode}
      selected={sel?.slot_id === s.slot_id}
      showCounts={!!s.product_id && (user?.role !== "stocker" || addMode)}
      canRemove={canEdit && !addMode}
      onClick={() => clickSlot(s)}
      onMinus={() => quickCount(s, -1)}
      onPlus={() => quickCount(s, 1)}
      onRemove={() => removeSlot(s.slot_id)}
    />
  );

  // Open a clean, printer-friendly planogram map in a new window so it can be
  // printed and taped inside the machine.
  const printPlanogram = () => {
    const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const rowsHtml = rowGroups.map(([letter, slots]) => {
      const label = rowLabel(letter);
      const col = rowColor(letter);
      const cells = slots.map((s) => {
        const p = s.product_id ? pmap[s.product_id] : null;
        const name = p ? esc(productLabel(p)) : "—";
        const size = p && p.size ? `<div class="size">${esc(p.size)}</div>` : "";
        const cur = s.current || 0;
        const par = s.par || 0;
        const deficit = Math.max(0, par - cur);
        const sell = p ? shelfPrice(p, globalMargin) : null;
        const priceTag = sell != null ? `<div class="price">${money(sell)}</div>` : "";
        const counts = `${priceTag}<div class="cnt">${cur} / ${par}</div>`;
        const load = p && deficit > 0 ? `<div class="load">load ${deficit}</div>` : (p ? `<div class="full">full</div>` : "");
        return `<div class="slot ${p ? "" : "empty"}"><div class="sid">${esc(s.slot_id)}</div><div class="pname">${name}</div>${size}${counts}${load}</div>`;
      }).join("");
      return `<div class="prow"><div class="rlabel"${col ? ` style="border-left:8px solid ${esc(col)}"` : ""}><div class="rl">${esc(letter)}</div>${label ? `<div class="rlbl">${esc(label)}</div>` : ""}</div><div class="slots">${cells}</div></div>`;
    }).join("");
    const doc = `<!doctype html><html><head><title>${esc(machine.name)} — Planogram</title>
      <style>
        *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#111}
        h1{font-size:20px;margin:0 0 2px} .sub{color:#555;font-size:12px;margin:0 0 6px}
        .legend{font-size:11px;color:#444;margin:0 0 14px} .lg-load{background:#b45309;color:#fff;border-radius:3px;padding:1px 4px;font-weight:700}
        .machine{max-width:1000px;margin:0 auto;border:3px solid #222;border-radius:10px;padding:10px;background:#eef1f3}
        .prow{display:flex;align-items:stretch;gap:6px;margin-bottom:6px}
        .prow:last-child{margin-bottom:0}
        .rlabel{width:84px;flex:0 0 84px;background:#fff;border:1px solid #999;border-radius:5px;padding:6px;display:flex;flex-direction:column;justify-content:center}
        .rl{font-weight:bold;font-size:18px} .rlbl{font-size:10px;color:#555;line-height:1.1}
        .slots{display:flex;flex:1;gap:6px}
        .slot{flex:1 1 0;min-width:0;border:1px solid #999;border-radius:5px;padding:6px 4px;background:#fff;display:flex;flex-direction:column;justify-content:space-between;text-align:center}
        .slot.empty{background:#f7f7f7}
        .sid{font-family:monospace;font-size:10px;color:#666}
        .pname{font-weight:bold;font-size:12px;margin:4px 0;word-break:break-word;line-height:1.15}
        .slot.empty .pname{color:#bbb;font-weight:normal}
        .size{font-size:10px;color:#333;font-weight:600}
        .price{display:inline-block;margin:3px auto 0;background:#fde68a;border-radius:3px;padding:0 4px;font-size:11px;font-weight:700}
        .cap{font-size:10px;color:#666;margin-top:4px}
        .cnt{font-size:12px;color:#111;font-weight:700;margin-top:4px}
        .load{font-size:11px;font-weight:700;color:#fff;background:#b45309;border-radius:3px;padding:1px 3px;margin-top:3px;display:inline-block}
        .full{font-size:10px;color:#15803d;font-weight:600;margin-top:3px}
        @media print{body{margin:10mm} .machine{background:#fff}}
      </style></head><body>
      <h1>${esc(machine.name)}</h1>
      <p class="sub">${esc(machine.model_name || "")} · ${(machine.slots || []).length} slots · ${esc(machine.location || "")}</p>
      <p class="legend"><b>on-hand / par</b> · <span class="lg-load">load N</span> = units to add to reach par</p>
      <div class="machine">${rowsHtml}</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(doc);
    w.document.close();
  };

  return (
    <div>
      <Link to="/admin/planner/machines" className="inline-flex items-center text-sm text-muted-foreground mb-3"><ArrowLeft className="mr-1 h-4 w-4" />Back to machines</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">{machine.name}</h1>
          <p className="text-sm text-muted-foreground">{machine.model_name} · {(machine.slots || []).length} slots · updated by {machine.updated_by || "—"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border p-0.5" role="group" aria-label="View">
            <Button size="sm" variant={view === "cooler" ? "default" : "ghost"} className="h-8" data-testid="view-cooler" onClick={() => changeView("cooler")}><Refrigerator className="mr-1.5 h-4 w-4" />Cooler</Button>
            <Button size="sm" variant={view === "grid" ? "default" : "ghost"} className="h-8" data-testid="view-grid" onClick={() => changeView("grid")}><LayoutGrid className="mr-1.5 h-4 w-4" />Grid</Button>
          </div>
          <Button variant={addMode ? "default" : "outline"} data-testid="add-stock-mode-toggle" onClick={() => setAddMode((v) => !v)}><Plus className="mr-2 h-4 w-4" />{addMode ? "Adding stock…" : "Add stock"}</Button>
          {canEdit && <Button variant="outline" data-testid="planogram-autofill-button" onClick={autoFill} disabled={autoFilling}><Wand2 className="mr-2 h-4 w-4" />{autoFilling ? "Filling…" : "Auto-fill"}</Button>}
          <Button variant="outline" data-testid="planogram-fill-par-button" onClick={fillToPar} disabled={!hasProducts}><CheckCheck className="mr-2 h-4 w-4" />Fill all to par</Button>
          {canEdit && (() => {
            const ids = [...new Set((machine.slots || []).map((s) => s.product_id).filter(Boolean))];
            const missing = ids.map((pid) => pmap[pid]).filter((p) => p && !fileUrl(p.image_path));
            return missing.length > 0 && <Button variant="outline" data-testid="planogram-photos-button" onClick={() => setPhotoFor({ queue: missing })}><ImageIcon className="mr-2 h-4 w-4" />Add photos ({missing.length})</Button>;
          })()}
          {canEdit && <SandstarLibraryAdd machine={machine} size="default" onDone={load} />}
          {canEdit && <Button variant="outline" data-testid="planogram-rows-button" onClick={() => setRowsOpen(true)}><SlidersHorizontal className="mr-2 h-4 w-4" />Rows</Button>}
          {canEdit && <Button variant="outline" data-testid="planogram-templates-button" onClick={openTemplates}><FolderOpen className="mr-2 h-4 w-4" />Templates</Button>}
          {user?.role === "admin" && <Button variant="outline" data-testid="planogram-save-starter-button" onClick={saveAsStarter}><Bookmark className="mr-2 h-4 w-4" />Save as starter</Button>}
          <Button variant="outline" data-testid="planogram-print-button" onClick={printPlanogram}><Printer className="mr-2 h-4 w-4" />Print</Button>
          {canEdit && (
            <div className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm text-muted-foreground" data-testid="planogram-save-status" title="Changes save automatically">
              {saving || dirty
                ? (<><Loader2 className="h-4 w-4 animate-spin text-primary" /><span data-testid="planogram-save-status-text">Saving…</span></>)
                : (<><Check className="h-4 w-4 text-green-600" /><span data-testid="planogram-save-status-text">All changes saved</span></>)}
            </div>
          )}
        </div>
      </div>

      <MachineRowsDialog machine={machine} open={rowsOpen} onClose={() => setRowsOpen(false)}
        beforeSave={async () => { if (dirty) await persistPlanogram(false); }}
        onSaved={(data) => { setMachine(data); setDirty(false); setRowsOpen(false); }} />

      {addMode && (
        <Card className="mb-4 flex items-center gap-2 border-green-300 bg-green-50/60 p-3 text-sm text-green-800" data-testid="add-stock-banner">
          <Plus className="h-4 w-4" />Add stock mode: tap any slot to add one unit — counts save automatically and par is untouched. Tap “Adding stock…” to finish.
        </Card>
      )}

      {!canEdit && <Card className="mb-4 flex items-center gap-2 p-3 text-sm text-muted-foreground"><Info className="h-4 w-4" />View-only: your role can see stocking layout but not edit assignments.</Card>}

      {picked && pmap[picked] && (
        <Card className="mb-3 flex items-center justify-between gap-2 border-sky-300 bg-sky-50 p-2.5 text-sm text-sky-900" data-testid="picked-banner">
          <span className="min-w-0 truncate">Tap a shelf spot to place <b>{productLabel(pmap[picked])}</b></span>
          <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => setPicked(null)}><X className="mr-1 h-3.5 w-3.5" />Done</Button>
        </Card>
      )}

      <DndContext sensors={sensors} collisionDetection={dropUnderPointer} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveDrag(null)}>
      {canEdit && !addMode && (
        <Card className="mb-3 p-3 lg:hidden" data-testid="planogram-palette-mobile">
          <div className="flex items-center gap-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Input value={paletteQuery} onChange={(e) => setPaletteQuery(e.target.value)} placeholder="Find an item, then tap a shelf spot" className="h-8 text-xs" />
          </div>
          {paletteControls}
          <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
            {paletteGroups.flatMap(([, items]) => items).map((p) => <PaletteItem key={p.id} p={p} compact price={shelfPrice(p, globalMargin)} picked={picked === p.id} onPick={togglePick} onFavorite={toggleFavorite} />)}
            {products.length === 0 && <p className="text-xs text-muted-foreground">No products yet. Add them under Products.</p>}
          </div>
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        {view === "cooler" ? (
          <Card className="p-2 sm:p-4">
            <CoolerView
              title={machine.model_name || machine.name}
              rowGroups={rowGroups}
              pmap={pmap}
              rowLabel={rowLabel}
              dndEnabled={dndEnabled}
              selectedId={sel?.slot_id}
              picking={!!picked}
              globalMargin={globalMargin}
              onSlotClick={clickSlot}
            />
          </Card>
        ) : (
        <Card className="p-4 overflow-x-auto">
          <div className="space-y-3 min-w-[760px]" data-testid="planogram-rows">
            {rowGroups.map(([letter, slots]) => (
              <div key={letter} className="flex items-start gap-2" data-testid={`planogram-row-${letter}`}>
                {rowColor(letter) && <span className="mt-5 h-9 w-1.5 shrink-0 rounded" style={{ background: rowColor(letter) }} data-testid={`planogram-row-color-${letter}`} />}
                <div className="mt-5 flex w-14 shrink-0 flex-col items-start gap-1">
                  <span className="font-mono text-sm font-bold text-primary">{letter}</span>
                  {rowLabel(letter) && <span className="text-[10px] leading-tight text-muted-foreground" data-testid={`planogram-row-label-${letter}`}>{rowLabel(letter)}</span>}
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 text-[10px] text-muted-foreground hover:text-primary"
                      data-testid={`row-fill-capacity-${letter}`}
                      title={`Set every par in row ${letter} to capacity${rowDepth(letter) ? ` (${rowDepth(letter)})` : ""}`}
                      onClick={() => setRowToCapacity(letter, slots)}
                    >
                      <ArrowUpToLine className="mr-0.5 h-3 w-3" />Cap
                    </Button>
                  )}
                </div>
                <div className="grid flex-1 gap-2" style={{ gridTemplateColumns: `repeat(${slots.length}, minmax(0, 1fr))` }}>
                  {slots.map((s) => slotCell(s))}
                </div>
              </div>
            ))}
          </div>
        </Card>
        )}

        {canEdit && (
          <Card className="p-4 hidden lg:block" data-testid="planogram-palette">
            <div className="flex items-center gap-2 font-heading font-semibold text-sm"><GripVertical className="h-4 w-4 text-primary" />Product palette</div>
            <p className="mt-1 text-xs text-muted-foreground">Drag a product onto a shelf spot, drag a spot onto another to move or swap, or click a product then click spots to place it.</p>
            <div className="relative mt-2">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="palette-search-input" value={paletteQuery} onChange={(e) => setPaletteQuery(e.target.value)} placeholder="Search name or size" className="h-8 pl-8 text-xs" />
            </div>
            {paletteControls}
            <div className="mt-3 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {products.length === 0 ? <p className="text-xs text-muted-foreground">No products yet.</p>
                : paletteGroups.length === 0 ? <p className="text-xs text-muted-foreground">{favOnly && !favCount ? "No favorites yet. Tap the star on a product to add it." : "No products match."}</p>
                : paletteGroups.map(([cat, items]) => {
                const cc = catColor(cat);
                return (
                  <div key={cat} data-testid={`palette-group-${cat}`}>
                    <div className="mb-1.5 flex items-center gap-1.5 sticky top-0 bg-card py-0.5">
                      {cat === "In backstock" ? <Package className="h-3 w-3 text-green-700" />
                        : cat === "Favorites" ? <Star className="h-3 w-3 fill-amber-400 text-amber-500" />
                        : <span className="h-2.5 w-2.5 rounded-full" style={{ background: cc.stripe }} />}
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{cat}</span>
                      <span className="text-[10px] text-muted-foreground">({items.length})</span>
                    </div>
                    <div className="space-y-1.5">
                      {(["type", "name"].includes(paletteSort) ? byProductName(items) : items.map((p) => ({ name: p.name, items: [p] })))
                        .map((g) => (g.items.length === 1
                          ? <PaletteItem key={g.items[0].id} p={g.items[0]} price={shelfPrice(g.items[0], globalMargin)} picked={picked === g.items[0].id} onPick={togglePick} onFavorite={toggleFavorite} />
                          : (
                            <div key={`${cat}-${g.name}`} className="rounded-md border border-dashed p-1.5" data-testid={`palette-name-${g.name}`}>
                              <div className="px-1 pb-1 text-xs font-semibold">{g.name} <span className="font-normal text-muted-foreground">({g.items.length})</span></div>
                              <div className="space-y-1">
                                {g.items.map((p) => <PaletteItem key={p.id} p={p} child price={shelfPrice(p, globalMargin)} picked={picked === p.id} onPick={togglePick} onFavorite={toggleFavorite} />)}
                              </div>
                            </div>
                          )))}
                    </div>
                  </div>
                );
              })}
            </div>
            {activeDrag?.type === "prod" && <div className="mt-2 rounded bg-primary/10 px-2 py-1 text-xs text-primary">Dragging: {activeDrag.product?.name}</div>}
          </Card>
        )}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <div className="pointer-events-none rounded-md border bg-card px-2 py-1.5 text-xs font-semibold shadow-lg ring-2 ring-primary" data-testid="drag-overlay">
            {activeDrag.product ? productLabel(activeDrag.product) : (activeDrag.slotId ? `Slot ${activeDrag.slotId}` : "")}
          </div>
        ) : null}
      </DragOverlay>
      </DndContext>

      <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
        {["Soda","Energy","Sports","Water","Juice","Tea","Chips","Candy","Protein","Other"].map((c) => {
          const col = catColor(c);
          return <span key={c} className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: col.stripe }} />{c}</span>;
        })}
      </div>

      <PhotoPicker
        open={!!photoFor}
        product={photoFor && !photoFor.queue ? (pmap[photoFor.id] || photoFor) : null}
        queue={photoFor?.queue}
        onClose={() => setPhotoFor(null)}
        onSaved={onPhotoSaved}
      />

      <SandstarPanel machine={machine} canEdit={canEdit} onSynced={load} />

      <LoadSheet rowGroups={rowGroups} pmap={pmap} globalMargin={globalMargin} />

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent data-testid="templates-dialog">
          <DialogHeader><DialogTitle>Loadout templates</DialogTitle>
            <DialogDescription>Save what's on these shelves under a name, or load a saved loadout into this machine.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input data-testid="template-name-input" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Gym mix, Office drinks" onKeyDown={(e) => e.key === "Enter" && saveTemplate()} />
            <Button data-testid="template-save-button" onClick={saveTemplate} disabled={!hasProducts}><Save className="mr-2 h-4 w-4" />Save</Button>
          </div>
          {!templates.some((t) => t.name.startsWith("Freezer sample")) && (
            <button type="button" className="rounded-md border border-dashed p-2.5 text-left text-xs text-muted-foreground hover:bg-muted" data-testid="template-add-samples" onClick={addSamples}>
              <b className="text-foreground">Add the sample loadouts</b>: Sandstar combo (soda and energy versions, from the Vendco setup guide) and a freezer (ice cream, meals, breakfast). Items not in your catalog are added with a suggested price and case size.
            </button>
          )}
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {templates.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">No templates yet.</p> : templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2 rounded-md border p-2.5" data-testid={`template-${t.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{t.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{t.item_count} spots · {t.model_name || "machine"}{t.saved_from ? ` · from ${t.saved_from}` : ""}</div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" data-testid={`template-apply-${t.id}`} onClick={() => applyTemplate(t)}>Load</Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Delete template" onClick={() => deleteTemplate(t)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="slot-assign-dialog">
          <DialogHeader><DialogTitle>Slot {sel?.slot_id}</DialogTitle>
            <DialogDescription>Assign a product and set the par and current count for this slot.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Product</Label>
              <Select value={selProd ? curName : "none"} onValueChange={pickName}>
                <SelectTrigger data-testid="slot-product-select"><SelectValue placeholder="Choose product" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Empty slot</SelectItem>
                  {productNames.map((n) => <SelectItem key={n} value={n}>{favNames.has(n) ? `★ ${n}` : n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {showFlavor && (
              <div><Label>Flavor</Label>
                <Select value={flVal(curFlavor)} onValueChange={(v) => pickFlavor(flRaw(v))}>
                  <SelectTrigger data-testid="slot-flavor-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {flavorOpts.map((f) => <SelectItem key={flVal(f)} value={flVal(f)}>{f || "No flavor"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {selProd && sizeOpts.length > 0 && (
              <div><Label>Size</Label>
                <Select value={szVal(curSize)} onValueChange={(v) => pickSize(szRaw(v))}>
                  <SelectTrigger data-testid="slot-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {sizeOpts.map((s) => <SelectItem key={szVal(s)} value={szVal(s)}>{s || "No size"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Par (intended)</Label><Input data-testid="slot-par-input" type="number" value={assign.par} onChange={(e) => setAssign({ ...assign, par: e.target.value })} /></div>
              <div><Label>Current count</Label><Input data-testid="slot-current-input" type="number" value={assign.current} onChange={(e) => setAssign({ ...assign, current: e.target.value })} /></div>
            </div>
            {selProd && (
              <div className="flex items-center gap-3 rounded-lg border p-2.5" data-testid="slot-photo">
                {fileUrl(selProd.image_path)
                  ? <img src={fileUrl(selProd.image_path)} alt="" className="h-14 w-14 rounded bg-white object-contain" />
                  : <div className="flex h-14 w-14 items-center justify-center rounded bg-muted text-[10px] text-muted-foreground">No photo</div>}
                <Button type="button" size="sm" variant="outline" onClick={() => setPhotoFor(selProd)}><ImageIcon className="mr-1.5 h-4 w-4" />{fileUrl(selProd.image_path) ? "Change photo" : "Find photo"}</Button>
              </div>
            )}
            {selProd && (
              <p className="rounded-lg border px-3 py-2 text-xs" data-testid="slot-backstock">
                <b>{selProd.on_hand || 0}</b> in backstock
                {selProd.last_purchase
                  ? <> · last bought {new Date(`${selProd.last_purchase.date}T12:00:00`).toLocaleDateString()} at {selProd.last_purchase.store} for {money(selProd.last_purchase.unit_cost)} each</>
                  : <span className="text-muted-foreground"> · no receipt yet. Add one under Stock Receipts to set its real cost.</span>}
              </p>
            )}
            {selProd && (() => {
              const m = price.margin !== "" ? Number(price.margin) : globalMargin;
              const suggested = priceForMargin(price.cost, m);
              const shown = price.vend !== "" ? Number(price.vend) : suggested;
              const earned = marginOnCost(price.cost === "" ? null : Number(price.cost), shown);
              return (
                <div className="rounded-lg border bg-muted/40 p-3" data-testid="slot-pricing">
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Tag className="h-3.5 w-3.5" />Price for {selProd.name}</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Cost ($)</Label><Input data-testid="slot-cost-input" type="number" step="0.01" inputMode="decimal" value={price.cost} onChange={(e) => setPrice({ ...price, cost: e.target.value })} placeholder="1.00" /></div>
                    <div><Label className="text-xs">Margin %</Label><Input data-testid="slot-margin-input" type="number" inputMode="numeric" value={price.margin} onChange={(e) => setPrice({ ...price, margin: e.target.value })} placeholder={markupLabel(globalMargin)} /></div>
                    <div><Label className="text-xs">Price ($)</Label><Input data-testid="slot-price-input" type="number" step="0.25" inputMode="decimal" value={price.vend} onChange={(e) => setPrice({ ...price, vend: e.target.value })} placeholder={suggested ? suggested.toFixed(2) : "auto"} /></div>
                  </div>
                  <p className="mt-2 text-xs" data-testid="slot-price-suggested">
                    {suggested
                      ? <>Suggested <b>{money(suggested)}</b> (cost + {markupFor(Number(price.cost), m)}%, rounded up to the next quarter){earned != null && <> · at {money(shown)} you make {money(shown - Number(price.cost))} ({earned}%)</>}</>
                      : <span className="text-muted-foreground">Enter a cost to get a suggested price.</span>}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {suggested > 0 && price.vend !== "" && Number(price.vend) !== suggested && <Button type="button" size="sm" variant="outline" onClick={() => setPrice({ ...price, vend: "" })}>Use {money(suggested)}</Button>}
                    <Button type="button" size="sm" variant="secondary" data-testid="slot-price-save" onClick={savePricing} disabled={savingPrice}>{savingPrice ? "Saving…" : "Save price"}</Button>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {sel?.product_id
              ? <Button variant="outline" className="text-destructive hover:bg-destructive/10" data-testid="slot-remove-button" onClick={() => removeSlot(sel.slot_id)}><Trash2 className="mr-2 h-4 w-4" />Remove item</Button>
              : <span />}
            <Button data-testid="slot-apply-button" onClick={applyAssign}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
